import { Capacitor } from '@capacitor/core';
import { useEffect } from 'react';

/**
 * The end of the road for this build.
 *
 * This version of the NIETE school app is no longer supported; the current one
 * ships on the Play Store. This screen is what every route resolves to once the
 * retirement bundle is live: a single, unskippable notice asking the teacher to
 * update.
 *
 * It is an update, not a migration — same app, same account, same data. The
 * copy deliberately says so and nothing more: a teacher reading "new app" would
 * reasonably wonder whether their work is coming with them, and that doubt is
 * the one thing this screen cannot afford to create.
 *
 * Why a page and not a hard block at boot:
 *
 *   - `main.tsx` is deliberately untouched. Capgo rolls a bundle back if
 *     `notifyAppReady()` is not called within ~10s of launch, so the app must
 *     keep booting normally. It does — the router simply has one destination.
 *   - Everything else (providers, network listener, push handlers) keeps
 *     working exactly as before. Nothing is ripped out; nothing can break in
 *     a way that strands a teacher on a white screen.
 *
 * There is no navigation off this page: no links, no back handler, no timers.
 * The Android back button minimises the app (ParentLayout's existing handler
 * sees `canGoBack === false`), which is the correct behaviour — it does not
 * reveal anything behind.
 *
 * Deliberately dependency-free: no design-system components, no DB or auth
 * reads, no network calls. This screen must render for a logged-out user, an
 * expired token, or an empty IndexedDB, on the oldest device in the field.
 *
 * On styling: the CSS ships as a `<style>` element rendered by this component,
 * not as a stylesheet import. That keeps the screen exactly as self-contained
 * as the inline styles it replaces — same bundle, no extra request, nothing to
 * fail to load — while allowing the things inline styles cannot express and
 * that this page genuinely needs: a visible focus ring, a pressed state on the
 * one button, a small-screen breakpoint, and a motion-reduced fallback. Every
 * class is `ar-` prefixed so it cannot collide with app or DSM styles, and the
 * element is rendered last so it wins ties against anything loaded earlier.
 *
 * Every declaration degrades rather than breaks on an old Android WebView: the
 * `env(safe-area-inset-*)` paddings sit after a plain `padding`, `100dvh` after
 * `100vh`, and gradients/animations simply do not paint if unsupported. The
 * notice itself is plain centred text either way.
 */

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=pk.edu.niete';

export const AppRetiredPage = () => {
  useEffect(() => {
    // A teacher who lands here should not be able to reach the old UI by
    // tapping back. Pinning a history entry keeps the stack at this page;
    // ParentLayout's existing handler then minimises the app rather than
    // navigating, because there is nothing left to go back to.
    const pin = () => window.history.pushState(null, '', window.location.href);
    pin();
    window.addEventListener('popstate', pin);
    return () => window.removeEventListener('popstate', pin);
  }, []);

  const openPlayStore = () => {
    // `market://` opens the Play app directly and avoids a browser hop; the
    // https URL is the fallback for any device without Play (and for the web
    // build, where `window.open` is the only sensible option).
    const marketUrl = 'market://details?id=pk.edu.niete';
    if (Capacitor.isNativePlatform()) {
      window.location.href = marketUrl;
      // If the market scheme is unhandled the page simply stays put, so give
      // the store URL a moment to take over as a fallback.
      window.setTimeout(() => {
        window.location.href = PLAY_STORE_URL;
      }, 600);
      return;
    }
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="ar-screen"
      data-testid="app-retired-page"
      data-cy="app-retired-page"
    >
      <style>{RETIREMENT_CSS}</style>

      <div className="ar-card">
        <div className="ar-badge">
          {/* Decorative: the heading carries the meaning. */}
          <svg
            className="ar-badge-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M12 15.4V4.3m0 0L7.7 8.6M12 4.3l4.3 4.3M5.4 19.7h13.2"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1 className="ar-heading">Please update your app</h1>

        <p className="ar-body">
          This version of the NIETE app is no longer supported. Please update to
          the latest version from the Play Store to continue.
        </p>

        <p className="ar-body ar-body-last">
          Your account and all your data stay exactly as they are.
        </p>

        <button
          type="button"
          className="ar-button"
          onClick={openPlayStore}
          data-cy="update-app-button"
        >
          <span>Update now</span>
          <svg
            className="ar-button-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M4.6 12h13.6m0 0l-5.1-5.1m5.1 5.1l-5.1 5.1"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <p className="ar-note">
          Search for <strong>NIETE</strong> on the Play Store if the button
          doesn&apos;t open it.
        </p>
      </div>
    </div>
  );
};

/**
 * Brand navy (#333748) sets the backdrop; the card is the only lit surface, so
 * the eye lands on the message and then on the single action. Text colours are
 * all ≥4.5:1 on white and the button is ~10:1, so the screen still reads in
 * direct sunlight on a dim, scratched phone screen — which is where most of
 * these teachers will see it.
 */
const RETIREMENT_CSS = `
.ar-screen {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-y: auto;
  padding: 24px;
  padding-top: calc(24px + env(safe-area-inset-top));
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
  background-color: #2b2f3f;
  background-image:
    radial-gradient(circle at 16% 10%, rgba(129, 141, 201, 0.32), rgba(129, 141, 201, 0) 58%),
    radial-gradient(circle at 88% 92%, rgba(242, 181, 68, 0.16), rgba(242, 181, 68, 0) 52%),
    linear-gradient(165deg, #3b4159 0%, #2b2f3f 54%, #21242f 100%);
  font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.ar-card {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 400px;
  /* Pairs with align-items: center — keeps the card from clipping off the top
     of a short landscape screen instead of scrolling. */
  margin: auto;
  padding: 34px 26px 26px;
  overflow: hidden;
  text-align: center;
  background-color: #ffffff;
  border-radius: 20px;
  box-shadow:
    0 1px 2px rgba(16, 18, 26, 0.14),
    0 18px 44px -14px rgba(10, 12, 18, 0.6);
  animation: ar-rise 420ms cubic-bezier(0.16, 0.84, 0.44, 1) both;
}

.ar-card::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 4px;
  background-image: linear-gradient(90deg, #333748 0%, #5f689b 52%, #f2b544 100%);
}

.ar-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  margin: 2px auto 20px;
  color: #ffffff;
  border-radius: 50%;
  background-image: linear-gradient(145deg, #4d5474 0%, #333748 100%);
  box-shadow:
    0 0 0 8px rgba(51, 55, 72, 0.05),
    0 10px 20px -8px rgba(51, 55, 72, 0.55);
}

.ar-badge-icon {
  width: 32px;
  height: 32px;
}

.ar-heading {
  margin: 0 0 14px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.28;
  letter-spacing: -0.3px;
  color: #1b1e29;
}

.ar-body {
  margin: 0 0 14px;
  font-size: 15px;
  line-height: 1.68;
  color: #565c70;
}

.ar-body-last {
  margin-bottom: 0;
}

.ar-button {
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 22px 0 0;
  padding: 15px 20px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.1px;
  color: #ffffff;
  background-image: linear-gradient(180deg, #434964 0%, #333748 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  touch-action: manipulation;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    0 8px 18px -8px rgba(51, 55, 72, 0.75);
  transition: transform 120ms ease, box-shadow 180ms ease, filter 180ms ease;
}

.ar-button-icon {
  width: 17px;
  height: 17px;
  /* margin, not flex gap — gap is unsupported on older Android WebViews. */
  margin-left: 9px;
}

.ar-button:hover {
  filter: brightness(1.09);
}

.ar-button:active {
  transform: translateY(1px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    0 3px 9px -5px rgba(51, 55, 72, 0.75);
}

.ar-button:focus {
  outline: 3px solid #f2b544;
  outline-offset: 3px;
}

.ar-button:focus:not(:focus-visible) {
  outline: none;
}

.ar-button:focus-visible {
  outline: 3px solid #f2b544;
  outline-offset: 3px;
}

.ar-note {
  margin: 22px 0 0;
  padding-top: 18px;
  font-size: 13px;
  line-height: 1.55;
  color: #6b7185;
  border-top: 1px solid #edeff4;
}

@keyframes ar-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (max-width: 360px) {
  .ar-card {
    padding: 28px 20px 22px;
    border-radius: 16px;
  }
  .ar-heading {
    font-size: 21px;
  }
  .ar-body {
    font-size: 14.5px;
  }
}

/* Landscape on a small phone: reclaim the vertical space the badge takes. */
@media (max-height: 520px) {
  .ar-badge {
    width: 52px;
    height: 52px;
    margin-bottom: 14px;
  }
  .ar-badge-icon {
    width: 25px;
    height: 25px;
  }
  .ar-card {
    padding-top: 26px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ar-card {
    animation: none;
  }
  .ar-button {
    transition: none;
  }
}
`;

export default AppRetiredPage;
